import React, { useEffect, useMemo, useState } from "react";
import {
    Area,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useSelector } from "react-redux";
import { useInventoryAvailabilityData, useStockOutAnddemandData } from "../../services/redux/hooks/useForecast";
import moment from "moment";
import { calculateData, formatNumber, formatter } from "../../utils/formatter";
import graphformat from "../../assets/graph.svg";
import tableformat from "../../assets/table.svg";
import { LineGraphSummary } from "./lineGraphSummary";
import { IRoot } from "../../services/interface/IHome";
import { ILegend, ITooltip } from "../../services/interface/IForecast";
import InfoOutlineIcon from '@rsuite/icons/InfoOutline';
import { Popover, Whisper } from "rsuite";

const CustomLegend = (props: any) => {
    const { payload, onLengendChange } = props;
    const [legendStatus, setLegendStatus] = useState<string[]>(["Last Year Demand","Year-2 Demand"]);
    const handleLegend = (data: any) => {
        let legendindex = legendStatus;
        if (["Last Year Demand","Year-2 Demand"].includes(data)) {
          if (legendindex.includes(data)) {
            legendindex = legendindex.filter(($: any) => $ !== data);
            setLegendStatus(legendindex);
          } else {
            setLegendStatus([...legendStatus, data]);
          }
        }
      };
    
    useEffect(()=>{
        if(onLengendChange){
            onLengendChange(legendStatus)
        }
    },[legendStatus]);
    return (
        <ul className="flex items-center gap-2">
            {payload.map((entry: any, index: number) => {
                if(!["bound"].includes(entry?.value)){
                    return <li onClick={()=>handleLegend(entry?.payload?.name)} key={`item-${index}`} className={`flex items-center mb-1 ${legendStatus.includes(entry?.payload?.name) ? "opacity-30" : ""}`}>
                        <svg width="30" height="8">
                            <line x1="0" y1="4" x2="10" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                            <polygon points="12,1 16,4 12,7 8,4" fill={entry?.color} />
                            <line x1="15" y1="4" x2="25" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                        </svg>
                        <span style={{color:entry?.color}} className="ml-2 font-semibold chart-legend">
                            {entry?.value}
                            {(onLengendChange && ["Last Year Demand","Year-2 Demand"].includes(entry?.payload?.name)) ? <Whisper
                                trigger="hover"
                                placement={"top"}
                                controlId={`control-id-top`}
                                speaker={
                                    <Popover {...props}>
                                        <p>Click on the legend to show/hide graph line </p>
                                    </Popover>
                                }>&nbsp;<InfoOutlineIcon /></Whisper> : ''}
                        </span>
                    </li>
                }
            })}
        </ul>
    );
};

const LineGraph = (props: {flag:number, title:string}) => {
    const [isActive, setIsActive] = useState<number>(0);
    const [loading, setLoading] = useState<Boolean>(false);
    const [legendStatus, setLegendStatus] = useState<string[]>(["Last Year Demand","Year-2 Demand"]);
    const [forecastGraphData, setForecastGraphData] = useState<[]>();
    const [graphStockAndAcurracyData, setGraphStockAndAcurracyData] = useState<[]>([]);
    const [graphStockOutInventory, setGraphStockOutInventory] = useState<[]>();
    const [gbpfilter, setGbpFilter] = useState<any>({
        plant: [],
        l2: [],
        l3: [],
        materialDescription: [],
        material: [],
        mrpType: [],
        futureSegmentation: [],
        vendorName: [],
        clustername: [],
        alerts: [],
        reviewedFlag: [],
        poAlert: [],
        adjustedLogic: []
    });
    const selectedGBPFilter = useSelector((state: IRoot) => state.filter.selectedGBPFilter);
    const { mutate: mutateGraphData, isError, error, isLoading, isSuccess, data: stockOutDemandData } = useStockOutAnddemandData();
    const { mutate: mutateInventoryGraphData, isError: isInventoryError, error: inventoryError, isSuccess: isInventorySuccess, data: inventoryGraphData } = useInventoryAvailabilityData();
    const alertType = new URLSearchParams(location.search).get("alert");
    const applyFilter = useSelector((state:IRoot)=>state.filter.applyForecastFilter);

    useEffect(() => {
        if(!applyFilter.status && applyFilter.rowNo == -1){
            const payload = {
                ...gbpfilter,
                ...selectedGBPFilter,
                alerts: [alertType],
            };
            mutateGraphData(payload);
        }
        else{
            if (applyFilter.status || applyFilter.rowNo === -1) {

                let existingAlerts = selectedGBPFilter?.alerts || [];
    
                if (!existingAlerts.includes(alertType)) {
                    existingAlerts = [...existingAlerts, alertType];
                }
                
                const payload = {
                    ...gbpfilter,
                    ...selectedGBPFilter,
                    alerts: existingAlerts,
                };
                mutateGraphData(payload);
            }
        } 
    }, [applyFilter, alertType]);

    useEffect(()=>{
        if(!applyFilter.status && applyFilter.rowNo == -1){
            if(isActive == 2){
                const payload = {
                    ...gbpfilter,
                    ...selectedGBPFilter,
                    alerts: [alertType],
                };
                mutateInventoryGraphData(payload);
            }
        }
        else{
            if(applyFilter.status || applyFilter.rowNo===-1){
                if(isActive == 2){
                    if (applyFilter.status || applyFilter.rowNo === -1) {

                        let existingAlerts = selectedGBPFilter?.alerts || [];
            
                        if (!existingAlerts.includes(alertType)) {
                            existingAlerts = [...existingAlerts, alertType];
                        }
                        
                        const payload = {
                            ...gbpfilter,
                            ...selectedGBPFilter,
                            alerts: existingAlerts,
                        };
                        mutateInventoryGraphData(payload);
                    }
                }
            }
        }
    },[isActive, applyFilter, alertType]);

    useEffect(() => {
        setLoading(true);
        if (stockOutDemandData) {
            let localstockOutDemandData = JSON.parse(JSON.stringify(stockOutDemandData));
            let sorted = localstockOutDemandData?.sdata.sort((a: any, b: any) => b.forecast_date - a.forecast_date);
            sorted.reverse()
            let finalData: any = []
            if (sorted?.length !== 0) {
                let isFuture = 0
                let cutoff = moment(localstockOutDemandData?.replicateDate?.[0]?.replicate_date?.slice(0,10)).startOf('month');
                sorted.forEach((element:any) => {
                    let obj={}
                    const thisMonth = moment(element?.forecast_date?.slice(0,10)).startOf('month');
                    let nextMonthCheck=moment(new Date(localstockOutDemandData?.replicateDate[0]?.replicate_date?.slice(0, 10)).setMonth(new Date(localstockOutDemandData?.replicateDate[0]?.replicate_date).getMonth() )+86400000).format("MMM YYYY")
                    if(isFuture===0&&nextMonthCheck!==moment(element?.forecast_date?.slice(0, 10)).format("MMM YYYY")){
                      element.toolforecast=element?.toolforecast ? element?.toolforecast:0
                      obj={
                        forecastDate:moment(element?.forecast_date?.slice(0, 10)).format("MMM'YY"),
                        accuracy:element?.accuracy ? (element?.accuracy*100).toFixed(2):0,
                        bias:element?.bias?(element?.bias*100).toFixed(2):0,
                        stockout_days:element?.stockout_days
                       
                      }
                      finalData.push(obj)
                    }
                    // STOP actualQty from Oct'24 onward
                    if (thisMonth.isSameOrAfter(cutoff)) {
                        element.actualQty = null;     // <— key line
                    }
                    if (
                        moment(localstockOutDemandData?.replicateDate[0]?.replicate_date?.slice(0, 10)).format("MMM YYYY")
                        === moment(element?.forecast_date?.slice(0, 10)).format("MMM YYYY")
                        || isFuture !== 0
                    ) {
                        isFuture += 1;
                        element.processedQty = null;
                        element.processedQtyLY = null;
                        element.processedQtyL2Y = null;
                        element.stockout_days = null;
                        element.bias = null;
                        element.accuracy = null;
                        element.actualQty = null;     // <— add this
                    }

                    if(isFuture<6){
                      element.forecastDate=moment(element?.forecast_date?.slice(0, 10)).format("MMM'YY")
                    }else{
                      element.toolforecast=null
                    }
                    if(moment(localstockOutDemandData?.replicateDate[0]?.replicate_date?.slice(0, 10)).format("MMM YYYY")===moment(element?.forecast_date?.slice(0, 10)).format("MMM YYYY")||isFuture!==0){
                      isFuture+=1
                      element.processedQty=null
                      element.processedQtyLY=null
                      element.processedQtyL2Y=null
                      element.stockout_days=null
                      element.bias=null
                      element.accuracy=null
                    }
                     
                  });
       
            }
            let filteredData = sorted?.filter((val: any) => val?.toolforecast);
            filteredData = calculateData(filteredData, "toolforecast");
            setForecastGraphData(filteredData);
            setGraphStockAndAcurracyData(finalData);
            setLoading(false);
        }
        else if(!stockOutDemandData){
            setForecastGraphData([]);
            setGraphStockAndAcurracyData([]);
            setLoading(false);
        }
    }, [stockOutDemandData]);

    useEffect(()=>{
        setLoading(true);
        if(inventoryGraphData){
            const localInventoryGraphData = JSON.parse(JSON.stringify(inventoryGraphData));
            localInventoryGraphData?.sdata.reverse().forEach((element: any) => {
                element.forecastDate = moment(element.weekstartdate?.slice(0, 10)).format("DD-MMM")
            });
            setGraphStockOutInventory(localInventoryGraphData?.sdata);
            setLoading(false);
        }
        else if(!inventoryGraphData){
            setGraphStockOutInventory([]);
            setLoading(false);
        }
    },[inventoryGraphData]);

    return (
        <div className="w-[100%] h-[100%] bg-[#FFF] shadow rounded px-[2%] py-[10px] border-[1px] border-[#BEBEBE] flex flex-col">
            <div className="flex justify-between items-center mb-[0.5vw]">
                <span className="text-mainGreen font-semibold chart-title self-start">
                    {props.flag === 1 ? props.title : 
                    props.flag === 2 ? isActive == 1 ? "Accuracy/Bias" : 
                    isActive == 2 ? "Inventory Availability for Last 12 Weeks" : props.title : props.title}
                </span>
                <div>
                    {props.flag == 1 && <div className="flex gap-[2px]">
                            <div
                                onClick={() => setIsActive(0)}
                                className={`${isActive == 0 ? "bg-deloitteGreen" : "bg-[#949494]"} 
                                rounded-tl-md rounded-bl-md p-[2px] pb-[3px] flex h-[20px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 0 ? "text-fs16 text-white font-semibold" : "text-fs14 font-semibold"}>
                                    <img src={graphformat} alt="graph" />
                                </span>
                            </div>
                            <div
                                onClick={() => setIsActive(1)}
                                className={`${isActive == 1 ? "bg-deloitteGreen" : "bg-[#949494]"} 
                                rounded-tr-md rounded-br-md p-[2px] pb-[3px] pr-[3px] flex h-[20px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 1 ? "text-fs14 text-white font-semibold" : "text-fs14 font-semibold"}>
                                    <img src={tableformat} alt="table" />
                                </span>
                            </div>
                        </div>
                    }
                    {props.flag == 2 && <div className="flex gap-[2px]">
                            <div
                                onClick={() => setIsActive(0)}
                                className={`${isActive == 0 ? "bg-deloitteGreen" : "bg-[#949494]"} 
                                rounded-tl-md rounded-bl-md p-[2px] pb-[3px] flex h-[20px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 0 ? "text-fs14 text-white font-semibold" : "text-fs14 font-semibold"}>Stock Out</span>
                            </div>

                            <div
                                onClick={() => setIsActive(1)}
                                className={`${isActive == 1 ? "bg-deloitteGreen" : "bg-[#949494]"} p-[2px] pb-[3px]
                                flex h-[20px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 1 ? "text-fs14 text-white font-semibold" : "text-fs14 font-semibold"}>Accuracy / Bias</span>
                            </div>

                            <div
                                onClick={() => setIsActive(2)}
                                className={`${isActive == 2 ? "bg-deloitteGreen" : "bg-[#949494]"} 
                                rounded-tr-md rounded-br-md p-[2px] pb-[3px] pr-[3px] flex h-[20px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 2 ? "text-fs14 text-white font-semibold" : "text-fs14 font-semibold"}>Inventory</span>
                            </div>
                        </div>
                    }
                </div>
            </div>
            <div className="flex h-[30vh]">
                {props.flag == 1 && isActive == 0 && <div className="flex-grow h-full">
                    <ResponsiveContainer width={"100%"} height={"100%"}>
                        <ComposedChart data={forecastGraphData} margin={{ top: 20, right: 20, bottom: 10, left: -20 }}>
                            <XAxis interval={1} fontSize={"0.8vw"} dataKey="forecastDate" type="category" stroke="#949494" />
                            <YAxis interval="equidistantPreserveStart" fontSize={"0.8vw"} tickCount={10} tickFormatter={formatter} stroke="#949494" />
                            <Tooltip content={<CustomTooltip/>} />
                            <Legend
                                content={<CustomLegend onLengendChange={(data:any)=>setLegendStatus(data)} />}
                                align="left"
                                verticalAlign="top"
                                iconSize={8}
                                wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                            />
                            <Line
                                name="CY Demand (Post outlier adj.)"
                                dataKey="processedQty"
                                stroke="#012169"
                                fill="#012169"
                                strokeWidth={2}
                                dot={{ strokeWidth: 1, r: 2 }}
                                activeDot={{ stroke: '#012169', fill: 'white', strokeWidth: 2, r: 4 }}
                            />
                            {alertType === "HighOutlierCorrection" && <Line
                                name="CY Demand (Before outlier adj.)"
                                dataKey="actualQty"
                                stroke="#0239b7"
                                fill="#0239b7"
                                connectNulls={false}  
                                strokeWidth={2}
                                dot={{ strokeWidth: 1, r: 2 }}
                                activeDot={{ stroke: '#0239b7', fill: 'white', strokeWidth: 2, r: 4 }}
                            />}
                            <Line
                                name="CY Forecast"
                                dataKey="toolforecast"
                                stroke="#86BC25"
                                fill="#86BC25"
                                strokeWidth={2}
                                dot={{ strokeWidth: 1, r: 2 }}
                                activeDot={{ stroke: '#86BC25', fill: 'white', strokeWidth: 2, r: 4 }}
                            />
                            <Line
                                name="Last Year Demand"
                                dataKey="processedQtyLY"
                                stroke="#0097A9"
                                fill="#0097A9"
                                strokeWidth={2}
                                hide={legendStatus.includes("Last Year Demand")}
                                dot={{ strokeWidth: 1, r: 2 }}
                            />
                            <Line
                                name="Year-2 Demand"
                                dataKey="processedQtyL2Y"
                                stroke="#62B5E5"
                                fill="#62B5E5"
                                strokeWidth={2}
                                hide={legendStatus.includes("Year-2 Demand")}
                                dot={{ strokeWidth: 1, r: 2 }}
                            />
                            <Area type="monotone"
                                dataKey="bound"
                                fill="#86BC25"
                                stroke="false"
                                activeDot={false}
                                dot={false}
                                fillOpacity={0.3} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>}
                {props.flag == 1 && isActive == 1 &&  <div className="flex-grow h-full">
                    <ResponsiveContainer width={"100%"} height={"100%"}>
                        <LineGraphSummary data={forecastGraphData} />
                    </ResponsiveContainer>
                </div>}
                {props.flag == 2 && (isActive == 0 || isActive == 1) && <div className="flex-grow">
                    <ResponsiveContainer width={"100%"} height={"100%"}>
                        <LineChart data={graphStockAndAcurracyData} margin={{ top: 20, right: 20, bottom: 10, left: -10 }}>
                            <XAxis interval={1} fontSize={"0.8vw"} dataKey="forecastDate" type="category" stroke="#949494" />
                            {isActive === 0 && <YAxis
                                interval="equidistantPreserveStart"
                                fontSize={"0.8vw"}
                                allowDecimals={false}
                                stroke="#949494"
                                ticks={[0, 8, 16, 24, 31]}
                                includeHidden
                                type="number"
                            />}
                            {isActive === 1 && <YAxis
                                interval="equidistantPreserveStart"
                                allowDecimals={false}
                                ticks={[100, 80, 60, 40, 20, 0, -20, -40, -60, -80, -100]}
                                includeHidden
                                type="number"
                                stroke="#949494"
                                fontSize={"0.8vw"}
                            />}
                            <Tooltip content={<CustomTooltip/>} />
                            <Legend
                                content={<CustomLegend />}
                                align="left"
                                verticalAlign="top"
                                iconSize={8}
                                wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                            />
                            {isActive == 0 && <Line
                                dataKey="stockout_days"
                                stroke="#0097A9"
                                name="Stock Out"
                                fill="#0097A9"
                                strokeWidth={2}
                                dot={{ strokeWidth: 1, r: 2 }}
                                activeDot={{ stroke: '#0097A9', fill: 'white', strokeWidth: 2, r: 4 }}
                            />}
                            {isActive === 1 && <ReferenceLine y={0} stroke="#949494" />}
                            {isActive === 1 && <Line
                                dataKey="accuracy"
                                stroke="#4ACB4A"
                                fill="#4ACB4A"
                                name="Accuracy"
                                strokeWidth={2}
                                dot={{ strokeWidth: 1, r: 2 }}
                                activeDot={{ stroke: '#4ACB4A', fill: 'white', strokeWidth: 2, r: 4 }}
                            />}
                            {isActive === 1 && <Line
                                dataKey="bias"
                                stroke="#006d00"
                                fill="#006d00"
                                name="Bias"
                                strokeWidth={2}
                                dot={{ strokeWidth: 1, r: 2 }}
                                activeDot={{stroke:'#006d00', fill: 'white', strokeWidth:2, r: 4}}
                            />
                            }
                        </LineChart>
                    </ResponsiveContainer>
                </div>}
                {props.flag == 2 && isActive == 2 && <div className="flex-grow">
                    <ResponsiveContainer width={"100%"} height={"100%"}>
                        <LineChart data={graphStockOutInventory} margin={{ top: 20, right: 20, bottom: 10, left: -10 }}>
                            <XAxis interval={1} fontSize={"0.8vw"} dataKey="forecastDate" type="category" stroke="#949494" />
                            <YAxis
                                fontSize={"0.8vw"}
                                interval="equidistantPreserveStart"
                                stroke="#949494"
                                tickFormatter={(value: any) =>
                                    value && `${(formatter(value))}`
                                }
                                type="number"
                            />
                            <Tooltip content={<CustomTooltip/>} />
                            <Legend
                                content={<CustomLegend />}
                                align="left"
                                verticalAlign="top"
                                iconSize={8}
                                wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                            />
                            <Line
                                dataKey="onhand_inventory"
                                stroke="#0097a9"
                                fill="#0097a9"
                                name="Inventory"
                                strokeWidth={2}
                                dot={{ strokeWidth: 1, r: 2 }}
                                activeDot={{stroke:'#0097a9', fill: 'white', strokeWidth:2, r: 4}}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>}
            </div>
        </div>
    );
}

const CustomTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#fff] border-[1px] border-[#BEBEBE] p-2 rounded rs-bg">
                <p className="text-black text-fs16 font-bold mb-3">
                    {payload[0]?.payload?.forecastDate}
                </p>
                <div className="grid grid-cols-[1fr_auto_auto] gap-2">
                    {payload?.map(($: ITooltip) => {
                        const truncatedString = String($?.name).length > 15 ? `${String($?.name).slice(0, 15)}...` : String($?.name);
                        if($?.name !== "bound"){
                            return <div className="flex flex-col" key={$?.name}>
                                <p className="text-black text-fs14">
                                    {truncatedString}
                                </p>
                                <p style={{color:$.color}} className="text-black text-fs20 font-bold">
                                    {$?.name === "Accuracy" || $.name === "Bias" ? 
                                    $.value + "%" : 
                                    ["Inventory","CY Forecast","CY Demand (Post outlier adj.)","CY Demand (Before outlier adj.)","Last Year Demand","Year-2 Demand"]
                                    .includes($?.name) ? 
                                    formatter($.value) : $.value}
                                </p>
                            </div>
                        }
                    })}
                </div>
            </div>
        );
    }
    return null;
};

export default LineGraph;
